/**
 * The playground log: every saved session, openable and exportable.
 *
 * Ops tests in the playground and their feedback needs the conversation it
 * refers to (Deivydas, 2026-09-07): this page lists the last 100 sessions
 * with who ran them, against which brand and account, and which order the
 * conversation ended up being about. "Open" restores the session into the
 * playground chat (same session id, so sending again CONTINUES it); the
 * download builds the same structured Markdown the chat's "Save as .md"
 * produces — the file ops attaches to feedback.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Copy, Download, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, apiGet } from '../api/client';
import { Badge, Button, Card, CardHeader, Table, Td, Th } from '../components/ui/primitives';
import {
  buildTranscriptMarkdown,
  downloadMarkdown,
  pairSessionTurns,
  transcriptFilename,
  type SessionRunOutcome,
} from '../lib/playground/transcript';

interface SessionRow {
  sessionId: string;
  /** The sequential playground ticket number (from 1000) feedback is filed under. */
  ticketNumber: number | null;
  brandSlug: string;
  brandName: string;
  openedAt: string;
  customerEmail: string | null;
  messages: number;
  author: string | null;
  orderId: string | null;
}

interface SessionDetail {
  sessionId: string;
  brandName: string;
  customerEmail: string | null;
  messages: Array<{ role: string; body: string; createdAt: string }>;
  /** Per-turn run outcomes, oldest first — why a turn has no reply. */
  runOutcomes?: SessionRunOutcome[];
}

/** One click puts "#1018" on the clipboard, ready to paste into feedback. */
function CopyTicket({ number }: { number: number }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`#${number}`);
    } catch {
      // The clipboard can be refused (permissions, insecure context). The
      // number is right there to select by hand, so failing quietly beats a
      // dialog for a convenience shortcut.
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`Copy ticket number ${number}`}
      title="Copy ticket number"
      className="text-ink-400 transition-colors duration-fast hover:text-ink-900"
    >
      {copied ? (
        <Check size={12} strokeWidth={2.5} aria-hidden className="text-ok-700" />
      ) : (
        <Copy size={12} strokeWidth={2} aria-hidden />
      )}
    </button>
  );
}

export function PlaygroundLogPage() {
  const navigate = useNavigate();
  const sessions = useQuery({
    queryKey: ['playground-sessions'],
    queryFn: () => apiGet<{ sessions: SessionRow[] }>('/api/playground/sessions'),
  });

  const download = useMutation({
    mutationFn: async (row: SessionRow) => {
      const detail = await apiGet<SessionDetail>(`/api/playground/sessions/${row.sessionId}`);
      // Pair the stored transcript the same way the restore does: a customer
      // message opens a turn, the reply that followed rides along, and the
      // run outcomes explain any silence (a blocked draft is shown as one).
      const turns = pairSessionTurns(detail.messages, detail.runOutcomes);
      const meta = {
        sessionId: row.sessionId,
        ticketNumber: row.ticketNumber,
        brandName: row.brandName,
        customerEmail: row.customerEmail,
        openedAt: row.openedAt,
        author: row.author,
      };
      downloadMarkdown(transcriptFilename(meta), buildTranscriptMarkdown(meta, turns));
    },
  });

  if (sessions.isPending) {
    return (
      <div className="mx-auto max-w-[1200px] px-5 py-5">
        <Card>
          <p className="px-4 py-6 text-sm text-ink-400">Loading…</p>
        </Card>
      </div>
    );
  }
  if (sessions.error) {
    return (
      <div className="mx-auto max-w-[1200px] px-5 py-5">
        <Card>
          <p className="px-4 py-6 text-sm text-stop-700">
            {sessions.error instanceof ApiError ? sessions.error.message : 'Something went wrong.'}
          </p>
        </Card>
      </div>
    );
  }

  const rows = sessions.data.sessions;

  return (
    <div className="mx-auto max-w-[1200px] px-5 py-5">
      <Card>
        <CardHeader
          title="Playground log"
          meta={`${rows.length} saved session(s), newest first`}
          action={<Badge tone="info">simulated — nothing here reached a customer</Badge>}
        />
        <Table>
          <thead>
            <tr>
              <Th>Ticket</Th>
              <Th>Date</Th>
              <Th>Tested by</Th>
              <Th>Brand</Th>
              <Th>Order</Th>
              <Th>Acting as</Th>
              <Th className="text-right">Msgs</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.sessionId}>
                <Td className="whitespace-nowrap font-mono text-xs font-semibold text-ink-900">
                  {row.ticketNumber != null ? (
                    <span className="flex items-center gap-1.5">
                      #{row.ticketNumber}
                      <CopyTicket number={row.ticketNumber} />
                    </span>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td className="whitespace-nowrap text-xs text-ink-500">
                  {new Date(row.openedAt).toLocaleString()}
                </Td>
                <Td className="text-xs text-ink-700">{row.author ?? '—'}</Td>
                <Td className="text-ink-900">{row.brandName}</Td>
                <Td className="font-mono text-xs text-ink-700">{row.orderId ?? '—'}</Td>
                <Td className="font-mono text-xs text-ink-500">{row.customerEmail ?? '—'}</Td>
                <Td className="tabular text-right text-ink-500">{row.messages}</Td>
                <Td>
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/playground?session=${row.sessionId}`)}
                    >
                      <ExternalLink size={13} strokeWidth={2} aria-hidden />
                      Open
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={download.isPending}
                      onClick={() => download.mutate(row)}
                    >
                      <Download size={13} strokeWidth={2} aria-hidden />
                      .md
                    </Button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-ink-400">
            No playground sessions yet — run one from the Playground page and it will appear here.
          </p>
        ) : null}
      </Card>
    </div>
  );
}
